import { useCallback, useEffect, useRef, useState } from 'react';

import { errorKey, isPending, type AuthApi, type PurchaseOrder,
         type Session } from '../auth/api';
import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import { PageFrame } from '../ui/PageFrame';
import { Sheet } from '../ui/Sheet';
import './shop.css';

/** 轮询间隔。PIX 到账通常几秒，太密只是白打服务端。 */
const POLL_MS = 3000;

/**
 * 收款界面。
 *
 * **复制粘贴码是主路径**，不是二维码。手机上的真实流程是：
 * 复制 → 切到银行 App → 粘贴 → 确认。二维码只有在"用另一台设备扫"
 * 的时候才有用，而那是桌面端的场景。
 *
 * 二维码**不在前端画**：渠道返回的 `payUrl` 就是它自己的收款页，上面有码。
 * 自己实现一个 QR 编码器的话，编错一位就是一张扫不出来的码，
 * 而"扫不出来"这件事只有玩家会发现 —— 那是最贵的一种 bug。
 *
 * 那个页面**在本页里嵌开**（`PageFrame`），不开新窗口 ——
 * 这是整个大厅的硬性要求，见 `ui/PageFrame.tsx` 开头。
 *
 * 到账靠**轮询**，不靠回调推给前端：回调是渠道 → 服务端的，
 * 浏览器这边没有那条连接。轮询到终态就停。
 */
export function PaySheet(
  { open, order, api, session, money, onClose, onPaid }:
  {
    open: boolean;
    order: PurchaseOrder | null;
    api: AuthApi;
    session: Session | null;
    /**
     * 带币种的金额格式化。**从外面传进来** —— 币种三件套
     * （代码/符号/小数位）在 game-server 那条链路上，这个面板拿不到，
     * 而"需支付 10"和"需支付 R$10.00"在付款界面上不是一回事。
     */
    money(n: number): string;
    onClose(): void;
    /** 到账了：外面要刷新余额和限购次数 */
    onPaid(o: PurchaseOrder): void;
  },
) {
  const { t } = useI18n();
  const [cur, setCur] = useState<PurchaseOrder | null>(order);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const [left, setLeft] = useState(0);
  const [frame, setFrame] = useState(false);
  // onPaid 只能叫一次 —— 不然每轮轮询都会去刷一次余额
  const told = useRef(false);

  useEffect(() => {
    setCur(order);
    setErr('');
    setCopied(false);
    told.current = false;
  }, [order]);

  const no = cur?.orderNo;
  const pending = !!cur && isPending(cur.status);

  // ---- 轮询到终态为止 ----
  const poll = useCallback(async () => {
    if (!session || !no) return;
    try {
      const o = await api.order(session.token, no);
      setCur(o);
      setErr('');
      if (o.status === 'credited' && !told.current) {
        told.current = true;
        onPaid(o);
      }
    } catch (e) {
      // **不要因为一次网络抖动就把界面变成错误页** ——
      // 下一轮大概率就好了，而这期间玩家可能正在银行 App 里付款。
      setErr(t(errorKey(e)));
    }
  }, [api, session, no, onPaid, t]);

  useEffect(() => {
    if (!open || !pending) return;
    const id = setInterval(() => void poll(), POLL_MS);
    return () => clearInterval(id);
  }, [open, pending, poll]);

  // ---- 倒计时 ----
  useEffect(() => {
    if (!open || !cur?.expiresAt) { setLeft(0); return; }
    const tick = () => setLeft(Math.max(0, Math.round(cur.expiresAt! - Date.now() / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [open, cur?.expiresAt]);

  async function copy() {
    const code = cur?.payCode;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // http 或者旧 webview 里没有 clipboard API。**不能就这么算了** ——
      // 复制不了的话整个收款流程就断了。
      const el = document.createElement('textarea');
      el.value = code;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); } finally { el.remove(); }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const mm = String(Math.floor(left / 60)).padStart(2, '0');
  const ss = String(left % 60).padStart(2, '0');

  return (
    <Sheet open={open} onClose={onClose} title={t('pay.title')}>
      {!cur ? null : cur.status === 'credited' ? (
        <div className="pay__done">
          <b className="pay__big">{t('pay.done')}</b>
          <p>{t('shop.getCoins', { v: money(cur.coins) })}</p>
          <Button variant="primary" onClick={onClose}>{t('action.close')}</Button>
        </div>
      ) : !pending ? (
        <div className="pay__done">
          {/* 过期/取消。**要说得出是哪一种** —— 一句"失败了"解释不了任何事 */}
          <b className="pay__big">{t(`pay.st.${cur.status}` as 'pay.st.expired')}</b>
          {cur.failReason && <p className="pay__why">{cur.failReason}</p>}
          <Button variant="primary" onClick={onClose}>{t('action.close')}</Button>
        </div>
      ) : (
        <>
          <div className="pay__head">
            <span>{t('pay.amount')}</span>
            <b>{money(cur.price)}</b>
          </div>
          <p className="pay__sub">{t('shop.getCoins', { v: money(cur.coins) })}</p>

          {left > 0 && (
            <p className="pay__timer">{t('pay.expiresIn', { v: `${mm}:${ss}` })}</p>
          )}

          {cur.payCode ? (
            <>
              {/* **主路径**：复制 → 切到银行 App → 粘贴 */}
              <Button variant="primary" className="pay__copy" onClick={copy}>
                {t(copied ? 'pay.copied' : 'pay.copy')}
              </Button>
              <p className="pay__hint">{t('pay.howto')}</p>
              <code className="pay__code">{cur.payCode}</code>
            </>
          ) : (
            <p className="pay__hint">{t('pay.noCode')}</p>
          )}

          {cur.payUrl && (
            // 二维码在渠道自己的收款页上。**在本页里嵌开，不开新窗口。**
            <Button variant="ghost" className="pay__link"
                    onClick={() => setFrame(true)}>
              {t('pay.openPage')}
            </Button>
          )}

          <p className="pay__waiting">
            {cur.status === 'paid' ? t('pay.crediting') : t('pay.waiting')}
          </p>
          {err && <p className="ref__err">{err}</p>}
          <p className="pay__no">{t('pay.orderNo')} <code>{cur.orderNo}</code></p>

          <PageFrame
            open={frame && !!cur.payUrl} url={cur.payUrl || ''}
            title={t('frame.pay')} onClose={() => setFrame(false)}
            // 嵌不进来时的退路就是 PIX 码 —— 它本来就是主路径
            fallback={(
              <>
                <Button variant="primary" className="pay__copy" onClick={copy}>
                  {t(copied ? 'pay.copied' : 'pay.copy')}
                </Button>
                <p className="pay__hint">{t('pay.howto')}</p>
                <code className="pay__code">{cur.payCode}</code>
              </>
            )} />
        </>
      )}
    </Sheet>
  );
}
